import React, { useMemo, useState } from 'react'
import SkillIcon from './SkillIcon'
import Lightbox from './Lightbox'

function buildSrcSet(sources) {
  return sources
    .filter(s => s.src && s.width) // ignore entries that cannot form a srcset (e.g., gifs without widths)
    .map(s => `${s.src} ${s.width}w`)
    .join(', ')
}

function sizesAttr() {
  return '(min-width: 1024px) 600px, (min-width: 640px) 80vw, 100vw'
}

export const ProjectCard = ({ project }) => {
  const [open, setOpen] = useState(false)
  const hero = project.hero || {}
  const heroSources = hero.sources || []
  const heroGifSrc = hero.gif?.src
  const isGif = Boolean(heroGifSrc || heroSources.some(s => s.src?.toLowerCase().endsWith('.gif')))

  const srcSet = useMemo(() => (isGif ? '' : buildSrcSet(heroSources)), [heroSources, isGif])
  const src = heroGifSrc || heroSources[1]?.src || heroSources[0]?.src
  const aspect = hero.aspectRatio || '16/9'

  const badges = [
    project.featured ? 'Featured' : null,
    project.inProgress ? 'In Progress' : null,
    project.openSource ? 'Open Source' : null,
  ].filter(Boolean)

  return (
    <article className="group rounded-xl border border-slate-200 dark:border-[#1e3a8a] bg-white dark:bg-[#0b1220] overflow-hidden shadow-sm hover:shadow-md focus-within:shadow-md transition-shadow">
      <button className="block w-full text-left" onClick={() => setOpen(true)} aria-label={`Open image for ${project.title}`}>
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: aspect, backgroundColor: hero.placeholderColor || '#0b1220' }}>
          <img
            src={src}
            srcSet={srcSet || undefined}
            sizes={isGif ? undefined : sizesAttr()}
            alt={hero.alt}
            loading="lazy"
            decoding={isGif ? 'auto' : 'async'}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </button>

      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-semibold leading-tight text-slate-900 dark:text-slate-700">
            {project.title}
            <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-600">{project.year}</span>
          </h3>
          <div className="flex gap-2 ml-auto">
            {badges.map(b => (
              <span key={b} className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-xs border border-slate-200">{b}</span>
            ))}
          </div>
        </div>

        <p className="mt-2 text-slate-700 dark:text-slate-500">{project.description}</p>

        {/* Tags + Skills as small pills next to project */}
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {[...(project.tags || []), ...(project.skills || [])].slice(0, 8).map((label) => (
            <span key={label} className="inline-flex items-center rounded-full bg-slate-100 dark:bg-[#10223c] border border-slate-200 dark:border-[#1e3a8a] px-2 py-0.5 text-xs text-slate-700 dark:text-slate-200" title={label}>
              {label}
            </span>
          ))}
          {((project.tags?.length || 0) + (project.skills?.length || 0)) > 8 && (
            <span className="text-xs text-slate-500">+{(project.tags?.length || 0) + (project.skills?.length || 0) - 8}</span>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap" aria-label="Project links">
          {project.links.map((link) => (
            <a key={link.type} href={link.url} className="inline-flex items-center gap-2 rounded-md border border-slate-500 dark:border-[#1e3a8a] px-3 py-1.5 text-sm hover:bg-slate-400 dark:hover:bg-[#10223c] focus:outline-none dark:text-slate-500" target="_blank" rel="noopener noreferrer">
              <SkillIcon name={link.type === 'live' ? 'live' : link.type} size={18} className="text-slate-600 dark:text-slate-500" />
              <span>{link.label || (link.type === 'live' ? 'Live Demo' : link.type === 'github' ? 'GitHub' : 'Case Study')}</span>
            </a>
          ))}
        </div>
      </div>

      <Lightbox open={open} onClose={() => setOpen(false)}>
        <img
          src={heroGifSrc || heroSources[heroSources.length - 1]?.src || src}
          srcSet={isGif ? undefined : srcSet || undefined}
          sizes={isGif ? undefined : '100vw'}
          alt={hero.alt}
          className="w-full h-auto max-h-[85vh] object-contain bg-black"
        />
      </Lightbox>
    </article>
  )
}

export default ProjectCard
